import React, { useState, useRef, useEffect } from "react";
import { publicApi } from "../api";
import CameraCapture from "./CameraCapture";
import normalizeImage from "../utils/normalizeImage";

/**
 * Selfie step — its OWN step, right after the licence capture. Shown whenever
 * the venue enables face match, INCLUDING when the member entered their details
 * manually (we still keep the selfie; there's just nothing to match against).
 *
 * If a licence was scanned (scanRef → a stored front image) the backend runs a
 * FOXID face match; otherwise it stores the selfie with status "not_performed".
 * Non-blocking: "Skip the selfie" records a "skipped" status and moves on.
 *
 *   scanRef:  the licence scan ref (null on manual entry)
 *   onDone:   (scanRef, status) => void   — status ∈ matched|failed|not_performed|skipped
 *   onBack:   () => void  — back to the licence step
 */
export default function StepSelfie({ slug, isExisting, scanRef, onDone, onBack }) {
  const [selfie, setSelfie] = useState(null); // { blob, url }
  const [camOpen, setCamOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const cancelledRef = useRef(false);
  const selfieRef = useRef(null);
  useEffect(() => { selfieRef.current = selfie; }, [selfie]);
  // Arm the in-flight guard + revoke the preview URL on unmount, so a submit
  // that resolves after the step is left doesn't setState on an unmounted tree.
  useEffect(() => () => { cancelledRef.current = true; if (selfieRef.current?.url) URL.revokeObjectURL(selfieRef.current.url); }, []);

  const setImage = (blob) => {
    setError("");
    if (selfie?.url) URL.revokeObjectURL(selfie.url);
    setSelfie({ blob, url: URL.createObjectURL(blob) });
    setCamOpen(false);
  };
  const onFile = async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setImage(await normalizeImage(f)); };

  const submit = async (withSelfie) => {
    if (withSelfie && !selfie) { setError("Please take a selfie, or skip this step."); return; }
    setBusy(true); setError("");
    try {
      const res = await publicApi.foxidSelfie(slug, {
        selfie: withSelfie ? selfie.blob : null,
        skip: !withSelfie,
        scanRef: scanRef || null,
        source: isExisting ? "renewal" : "signup",
      });
      if (cancelledRef.current) return;
      onDone(res.scanRef || scanRef || null, res.status || (withSelfie ? "not_performed" : "skipped"));
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err.message || "We couldn't process your photo. Please retake it, or skip this step.");
    } finally {
      if (!cancelledRef.current) setBusy(false);
    }
  };

  return (
    <>
      <h2 className="step-title">Take a selfie</h2>
      <p className="step-sub">
        Take a quick selfie to complete your membership. If you scanned your licence, we'll match it to the photo
        to confirm it's really you. Face the camera in good light, then a 3-second timer takes the shot.
      </p>

      {error && <div className="su-alert error" style={{ marginBottom: "1rem" }}>⚠ {error}</div>}

      <div className="su-selfie-wrap">
        {selfie ? (
          <div className="su-selfie-preview">
            <img src={selfie.url} alt="Your selfie" />
          </div>
        ) : (
          <button type="button" className="su-selfie-empty" onClick={() => { setError(""); setCamOpen(true); }} disabled={busy}>
            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
            </svg>
            <span className="su-selfie-cta">Tap to take a selfie</span>
          </button>
        )}
        <div className="su-licence-links" style={{ justifyContent: "center" }}>
          <button type="button" className="su-link-btn" onClick={() => { setError(""); setCamOpen(true); }} disabled={busy}>
            {selfie ? "Retake" : "Open camera"}
          </button>
          <button type="button" className="su-link-btn" onClick={() => fileRef.current?.click()} disabled={busy}>Upload</button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} />
      </div>

      <div className="su-btn-row" style={{ marginTop: "1.25rem" }}>
        <button type="button" className="su-btn su-btn-outline" onClick={onBack} disabled={busy}>Back</button>
        <button type="button" className="su-btn su-btn-primary" onClick={() => submit(true)} disabled={busy || !selfie}>
          {busy ? "Please wait…" : "Continue"}
        </button>
      </div>

      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <button type="button" onClick={() => submit(false)} disabled={busy}
          style={{ background: "none", border: "none", color: "var(--su-text, #475569)", opacity: 0.8, fontSize: "0.85rem", textDecoration: "underline", cursor: busy ? "default" : "pointer" }}>
          Skip the selfie
        </button>
      </div>

      {camOpen && (
        <CameraCapture
          variant="selfie"
          title="Selfie"
          hint="Face the camera, fit your face in the oval, and hold still."
          onCapture={setImage}
          onClose={() => setCamOpen(false)}
        />
      )}
    </>
  );
}
